import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import FilterableSkuList, {
  SkuListColumn,
  SkuListFilter,
} from "components/FilterableSkuList";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Navigate, useParams } from "react-router-dom";
import contextStore from "stores/contextStore";

const submissionAuthoritySkuFilters = [
  SkuListFilter.Search,
  SkuListFilter.Dates,
  SkuListFilter.SkuFormat,
  SkuListFilter.SkuAvailability,
  SkuListFilter.SkuStatus,
  SkuListFilter.ApprovalStatus,
];

const SubmissionAuthoritySkus = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const { submissionAuthorityService, submissionAuthorityDetailsUrl } =
    useGlobalContext();
  const { organizationId } = contextStore.useState();

  const submissionAuthorityResult = useQuery(
    ["submissionAuthorityService.getAuthorityTypeById", id, organizationId],
    () => submissionAuthorityService.getAuthorityTypeById(id!),
    {
      enabled: !!id,
    },
  );

  const submissionAuthority = submissionAuthorityResult.data?.data;
  useTitle(submissionAuthority?.name || t({ id: "submissionAuthorities" }));

  const qualificationIds = (submissionAuthority?.accreditationChain ?? [])
    .map((c) => c.accreditationBodyId)
    .filter((qid): qid is string => !!qid);

  if (!id) {
    return <Navigate to={submissionAuthorityDetailsUrl(id)} replace />;
  }
  if (submissionAuthorityResult.isLoading) {
    return <Loader />;
  }
  if (submissionAuthorityResult.isError || !submissionAuthority) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="submissionAuthorityNotAccessible" />
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={submissionAuthorityDetailsUrl(id)}
        labelKey="backToSubmissionAuthority"
      />
      <h1>
        <u>
          <T id="associatedSkus" />
        </u>
        : {submissionAuthority.name}
      </h1>
      {qualificationIds.length === 0 ? (
        <Alert severity="info">{t({ id: "accreditationChainEmpty" })}</Alert>
      ) : (
        <FilterableSkuList
          organizationId={organizationId}
          lazyTableTitle="associatedSkus"
          preSelectedQualificationIds={qualificationIds}
          columns={[
            SkuListColumn.Sku,
            SkuListColumn.Title,
            SkuListColumn.SkuFormat,
            SkuListColumn.SkuAvailability,
            SkuListColumn.Status,
          ]}
          filters={submissionAuthoritySkuFilters}
        />
      )}
    </>
  );
};

export default SubmissionAuthoritySkus;
